import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { Toaster } from "sonner";

// Pages
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import DashboardPage from "./pages/DashboardPage";
import OwnerDashboardPage from "./pages/OwnerDashboardPage";
import ClonesPage from "./pages/ClonesPage";
import TimelinePage from "./pages/TimelinePage";
import CloneChatPage from "./pages/CloneChatPage";
import GlobalChatPage from "./pages/GlobalChatPage";
import AddMemoryPage from "./pages/AddMemoryPage";
import EmailSourcePage from "./pages/EmailSourcePage";
import RingoverSourcePage from "./pages/RingoverSourcePage";
import AircallSourcePage from "./pages/AircallSourcePage";
import TeamsSourcePage from "./pages/TeamsSourcePage";
import SourcesPage from "./pages/SourcesPage";
import ContactsPage from "./pages/ContactsPage";
import UsersPage from "./pages/UsersPage";
import SettingsPage from "./pages/SettingsPage";
import VoiceChatPage from "./pages/VoiceChatPage";
import OrganizationsPage from "./pages/OrganizationsPage";
import DossiersPage from "./pages/DossiersPage";
import DossierDetailPage from "./pages/DossierDetailPage";
import DepartmentsPage from "./pages/DepartmentsPage";
import CloneBrainPage from "./pages/CloneBrainPage";
import SuiviPage from "./pages/SuiviPage";
import SuggestionsPage from "./pages/SuggestionsPage";
// Platform Admin Pages
import PlatformDashboardPage from "./pages/PlatformDashboardPage";
import PlatformTenantsPage from "./pages/PlatformTenantsPage";
import PlatformUsersPage from "./pages/PlatformUsersPage";
import PlatformSourcesPage from "./pages/PlatformSourcesPage";
import PlatformJobsPage from "./pages/PlatformJobsPage";
import PlatformDiagnosticPage from "./pages/PlatformDiagnosticPage";
import PlatformTenantDetailPage from "./pages/platform/PlatformTenantDetailPage";
import PlatformSecurityPage from "./pages/PlatformSecurityPage";
import PlatformDocsPage from "./pages/PlatformDocsPage";

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { isAuthenticated, loading, user } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (adminOnly && user?.role !== 'owner' && user?.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Platform Admin Route — super_admin only
const PlatformRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!user?.is_platform_admin) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Public Route Component (redirects based on role)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }
  
  if (isAuthenticated) {
    if (user?.is_platform_admin) {
      return <Navigate to="/platform" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={
        <PublicRoute>
          <LoginPage />
        </PublicRoute>
      } />
      <Route path="/register" element={
        <PublicRoute>
          <RegisterPage />
        </PublicRoute>
      } />
      
      {/* Protected Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <DashboardPage />
        </ProtectedRoute>
      } />
      <Route path="/activity" element={
        <ProtectedRoute adminOnly>
          <OwnerDashboardPage />
        </ProtectedRoute>
      } />
      <Route path="/brain" element={
        <ProtectedRoute adminOnly>
          <CloneBrainPage />
        </ProtectedRoute>
      } />
      <Route path="/suivi" element={
        <ProtectedRoute>
          <SuiviPage />
        </ProtectedRoute>
      } />
      <Route path="/suggestions" element={
        <ProtectedRoute>
          <SuggestionsPage />
        </ProtectedRoute>
      } />
      <Route path="/clones" element={
        <ProtectedRoute>
          <ClonesPage />
        </ProtectedRoute>
      } />
      <Route path="/clones/:personId/chat" element={
        <ProtectedRoute>
          <CloneChatPage />
        </ProtectedRoute>
      } />
      <Route path="/chat/global" element={
        <ProtectedRoute>
          <GlobalChatPage />
        </ProtectedRoute>
      } />
      <Route path="/clones/:personId/voice" element={
        <ProtectedRoute>
          <VoiceChatPage />
        </ProtectedRoute>
      } />
      <Route path="/clones/:personId/timeline" element={
        <ProtectedRoute>
          <TimelinePage />
        </ProtectedRoute>
      } />
      <Route path="/clones/:personId/memories/new" element={
        <ProtectedRoute>
          <AddMemoryPage />
        </ProtectedRoute>
      } />
      <Route path="/clones/:personId/email" element={
        <ProtectedRoute>
          <EmailSourcePage />
        </ProtectedRoute>
      } />
      <Route path="/clones/:personId/ringover" element={
        <ProtectedRoute>
          <RingoverSourcePage />
        </ProtectedRoute>
      } />
      <Route path="/clones/:personId/aircall" element={
        <ProtectedRoute>
          <AircallSourcePage />
        </ProtectedRoute>
      } />
      <Route path="/clones/:personId/teams" element={
        <ProtectedRoute>
          <TeamsSourcePage />
        </ProtectedRoute>
      } />
      <Route path="/clones/:personId/sources" element={
        <ProtectedRoute>
          <SourcesPage />
        </ProtectedRoute>
      } />
      <Route path="/contacts" element={
        <ProtectedRoute>
          <ContactsPage />
        </ProtectedRoute>
      } />
      <Route path="/organizations" element={
        <ProtectedRoute adminOnly>
          <OrganizationsPage />
        </ProtectedRoute>
      } />
      <Route path="/dossiers" element={
        <ProtectedRoute>
          <DossiersPage />
        </ProtectedRoute>
      } />
      <Route path="/dossiers/:id" element={
        <ProtectedRoute>
          <DossierDetailPage />
        </ProtectedRoute>
      } />
      <Route path="/users" element={
        <ProtectedRoute adminOnly>
          <UsersPage />
        </ProtectedRoute>
      } />
      <Route path="/departments" element={
        <ProtectedRoute adminOnly>
          <DepartmentsPage />
        </ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute adminOnly>
          <SettingsPage />
        </ProtectedRoute>
      } />
      
      {/* Platform Admin Routes */}
      <Route path="/platform" element={<PlatformRoute><PlatformDashboardPage /></PlatformRoute>} />
      <Route path="/platform/tenants" element={<PlatformRoute><PlatformTenantsPage /></PlatformRoute>} />
      <Route path="/platform/tenants/:tenantId" element={<PlatformRoute><PlatformTenantDetailPage /></PlatformRoute>} />
      <Route path="/platform/users" element={<PlatformRoute><PlatformUsersPage /></PlatformRoute>} />
      <Route path="/platform/sources" element={<PlatformRoute><PlatformSourcesPage /></PlatformRoute>} />
      <Route path="/platform/jobs" element={<PlatformRoute><PlatformJobsPage /></PlatformRoute>} />
      <Route path="/platform/security" element={<PlatformRoute><PlatformSecurityPage /></PlatformRoute>} />
      <Route path="/platform/docs" element={<PlatformRoute><PlatformDocsPage /></PlatformRoute>} />
      <Route path="/platform/diagnostic" element={<PlatformRoute><PlatformDiagnosticPage /></PlatformRoute>} />

      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
